import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import environment from "../../environments";
import TurnstileWidget from "../../components/UI/TurnstileWidget/TurnstileWidget.tsx";
import type { TurnstileWidgetHandle } from "../../components/UI/TurnstileWidget/TurnstileWidget.tsx";
import HeroSection from "../../components/WorkBag/HeroSection.tsx";
import StickyNavbar from "../../components/WorkBag/StickyNavbar.tsx";
import WorkBagForm from "./WorkBagForm.tsx";
import WorkOfferGrid from "../../components/WorkBag/WorkOfferGrid.tsx";
import useGetTodayDate from "../../hooks/useGetTodayDate";
import { useScrollDetection } from "../../hooks/useScrollDetection.ts";
import { getSkills, createWorkBagUser, openIdentityModal, getAllWorkOffers } from "../../store/actions";
import { fireAnalytics } from "../../utils/firebase";
import Logo from "../../assets/images/logo-navbar.png";
import { getSessionId, getDeviceCategory, getPreviousPage } from "../../utils/analyticsHelpers.ts";
import styles from "./WorkBagNew.module.scss";
import { 
  WorkBagProps, 
  WorkBagFormValues, 
  RootState 
} from "../../types/workBag";

type SectionType = "offers" | "form";

const WorkBag: React.FC<WorkBagProps> = React.memo(({ 
  getSkills, 
  skills, 
  createWorkBagUser, 
  allJobRequests,
  getAllWorkOffers
}) => {
  const [activeSection, setActiveSection] = useState<SectionType>("offers");
  const [isPhone, setIsPhone] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [captchaReady, setCaptchaReady] = useState(false);
  const useCaptcha = Boolean(environment.turnstile?.siteKey);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);
  const turnstileTokenRef = useRef<string | null>(null);
  const offersSectionRef = useRef<HTMLElement>(null);
  const formSectionRef = useRef<HTMLElement>(null);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const { isScrolled, scrollY } = useScrollDetection(100);


  
  const today = useGetTodayDate();
  const navigate = useNavigate();

  const handleResize = useCallback(() => {
    const windowWidth = window.innerWidth;
    const wasPhone = isPhone;
    const newIsPhone = windowWidth <= 900;
    setIsPhone(newIsPhone); 
    

    if (wasPhone !== newIsPhone) {
      setIsCompact(false);
    }
  }, [isPhone]);

  useEffect(() => {
    getSkills();
    getAllWorkOffers();
    
    
    // Nuevo evento estandarizado
    fireAnalytics("page_work_bag_view", {
      session_id: getSessionId(),
      source: getPreviousPage() ? "navigation" : "direct",
      previous_page: getPreviousPage(),
      device_category: getDeviceCategory(),
      category: "navigation",
      action: "page_view",
      page_name: "work_bag"
    });
    
    handleResize();
  }, [getSkills, getAllWorkOffers, handleResize]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  const lockScroll = isPhone && !isCompact;

  useEffect(() => {
    if (!lockScroll) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [lockScroll]);

  const initialValues: WorkBagFormValues = {
    name: "",
    lastname: "",
    email: "",
    phone: "",
    skills: [],
    workZone: [],
    city: "",
    unemploymentFund: false,
    currentJob: false,
    employmentEndDate: "",
    termsAndConditions: false,
    selectedJurisdiction: null,
  };

  const telRegex = /[0-9+]/;

  const formSchema = Yup.object({
    name: Yup.string().required("Nombre*"),
    lastname: Yup.string().required("Apellido*"),
    email: Yup.string().email("Email invalido").required("Email*"),
    phone: Yup.string()
      .matches(telRegex, "*Solo se permiten números")
      .min(6, "*Número no válido")
      .required("Teléfono*"),
    skills: Yup.array().required("Debes seleccionar al menos un rubro de trabajo*"),
    workZone: Yup.array().required("Elige al menos una provincia*"),
    unemploymentFund: Yup.boolean().required("*Campo obligatorio"),
    currentJob: Yup.boolean().required("*Campo obligatorio"),
    employmentEndDate: Yup.string().when("currentJob", {
      is: true,
      then: Yup.string().required("*Debes seleccionar una fecha aprox."),
      otherwise: Yup.string(),
    }),
    selectedJurisdiction: Yup.object().nullable(),
    termsAndConditions: Yup.boolean().oneOf([true], "*Debes aceptar los términos y condiciones"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: formSchema,
    onSubmit: async (values) => {
      try {
        if (useCaptcha) {
          const token = turnstileTokenRef.current;
          if (!token) {
            alert("Completá la verificación antes de enviar.");
            return;
          }
          await createWorkBagUser({ ...values, tokenRecaptcha: token }, navigate);
          formik.setErrors({});
          formik.resetForm();
          setCaptchaReady(false);
          turnstileTokenRef.current = null;
          turnstileRef.current?.reset();
          return;
        }

        await createWorkBagUser({ ...values, tokenRecaptcha: "" }, navigate);
        formik.setErrors({});
        formik.resetForm();
      } catch (error) {
        console.error("Error al enviar el formulario:", error);
        alert("Ocurrió un error al validar el formulario. Por favor intentá de nuevo.");
      }
    },
  });

  const handleTurnstileVerify = useCallback((token: string) => {
    turnstileTokenRef.current = token;
    setCaptchaReady(true);
  }, []);

  const handleTurnstileError = useCallback(() => {
    turnstileTokenRef.current = null;
    setCaptchaReady(false);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    turnstileTokenRef.current = null;
    setCaptchaReady(false);
  }, []);

  const showCaptcha = useCaptcha && environment.turnstile.siteKey && formik.isValid && formik.dirty;

  useEffect(() => {
    if (!showCaptcha) setCaptchaReady(false);
  }, [showCaptcha]);

  const scrollToSection = useCallback((section: SectionType) => {
    setActiveSection(section);

    if (section === "offers") {
      fireAnalytics("page_work_offers_view", {
        session_id: getSessionId(),
        source: "navigation",
        previous_page: "/bolsa-trabajo",
        device_category: getDeviceCategory(),
        category: "navigation",
        action: "page_view",
        page_name: "work_offers",
      });
    }

    if (isPhone) {
      setIsCompact(true);
    }

    setTimeout(() => {
      const ref = section === "offers" ? offersSectionRef : formSectionRef;
      const el = ref.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const offset = section === "form" ? 120 : 100;
        const targetScrollY = window.scrollY + rect.top - offset;
        window.scrollTo({ top: targetScrollY, behavior: "smooth" });
      }
    }, isPhone ? 100 : 50);
  }, [isPhone]);

  return (
    <div className={styles.mainContainer} ref={mainContainerRef}>

      {isScrolled && (!isPhone || !isCompact) && (
        <StickyNavbar
          activeSection={activeSection}
          onScrollToSection={scrollToSection}
          logo={Logo}
          totalOffers={allJobRequests?.total ?? 0}
        />
      )}

      <HeroSection
        logo={Logo}
        isScrolled={isScrolled}
        scrollY={scrollY}
        onScrollToSection={scrollToSection}
        activeSection={activeSection}
        totalOffers={allJobRequests?.total ?? 0}
        isMobile={isPhone}
        isCompact={isCompact}
      />

      <main className={`${styles.mainContent} ${isPhone && isCompact ? styles.mainContent_withCompactHero : ""} ${isPhone && !isCompact ? styles.mainContent_withFullHero : ""}`}>
        <div className={styles.contentContainer}>
          <section ref={offersSectionRef} className={styles.offersSection}>
            <h2 className={styles.sectionTitle}>Ofertas disponibles</h2>
            <WorkOfferGrid scrollToForm={() => scrollToSection("form")} />
          </section>
          <section ref={formSectionRef} className={styles.formSection}>
            <div className={styles.formSectionHeader}>
              <h2 className={styles.sectionTitle}>Inscribite para postularte</h2>
              <p className={styles.formSectionSubtitle}>
                Completá tus datos gratis y accedé a todas las ofertas. Te avisamos por email cuando haya ofertas para vos.
              </p>
            </div>
            <WorkBagForm
              formik={formik}
              skills={skills}
              today={today}
              isPhone={isPhone}
              scrollToOffers={() => scrollToSection("offers")}
              captchaReady={!useCaptcha || captchaReady}
              useCaptcha={useCaptcha}
              validatingCaptcha={useCaptcha && showCaptcha && !captchaReady}
            />
          </section>
        </div>
      </main>


      {showCaptcha && (
        <div className={styles.turnstileWrapper}>
          <TurnstileWidget
            ref={turnstileRef}
            sitekey={environment.turnstile.siteKey}
            onVerify={handleTurnstileVerify}
            onExpire={handleTurnstileExpire}
            onError={handleTurnstileError}
            appearance="interaction-only"
            size="normal"
          />
        </div>
      )}
    </div>
  );
});

const mapStateToProps = (state: RootState) => {
  return {
    skills: state.register.skills,
    allJobRequests: state.register.allJobRequests,
  };
};

export default connect(mapStateToProps, { getSkills, createWorkBagUser, openIdentityModal, getAllWorkOffers })(WorkBag);
